import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Box,
  Flex,
  IconButton,
  Text,
  Spinner,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { CloseIcon } from '@chakra-ui/icons';

export type TermsDisplayMode = 'popup' | 'fullscreen';

export interface TermsProps {
  documentPath: string;
  title: string;
  isOpen: boolean;
  onClose: () => void;
  displayMode?: TermsDisplayMode;
  showCloseButton?: boolean;
  onAccept?: () => void;
  onDecline?: () => void;
  showAcceptDecline?: boolean;
  acceptText?: string;
  declineText?: string;
}

const Terms: React.FC<TermsProps> = ({
  documentPath,
  title,
  isOpen,
  onClose,
  displayMode = 'popup',
  showCloseButton = true,
  onAccept,
  onDecline,
  showAcceptDecline = false,
  acceptText = 'Accept',
  declineText = 'Decline',
}) => {
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (isOpen && documentPath) {
      loadDocument();
    }
  }, [isOpen, documentPath]);

  const loadDocument = async () => {
    setLoading(true);
    setError('');
    
    try {
      // Fetch the HTML file directly from the public assets
      const response = await fetch(`/system-docs/${documentPath}`);
      if (!response.ok) {
        throw new Error(`Failed to load document: ${response.statusText}`);
      }
      
      let htmlContent = await response.text();
      // Temporarily replace existing mailto links with placeholders
      const mailtoPlaceholders: string[] = [];
      htmlContent = htmlContent.replace(
        /<a href="mailto:([^"]+)">([^<]+)<\/a>/g,
        (match, href, text) => {
          const placeholder = `__MAILTO_PLACEHOLDER_${mailtoPlaceholders.length}__`;
          mailtoPlaceholders.push(match);
          return placeholder;
        }
      );
      
      // Wrap remaining email addresses in mailto links
      htmlContent = htmlContent.replace(
        /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g,
        '<a href="mailto:$1" class="terms-email-link">$1</a>'
      );
      
      // Restore the original mailto links
      mailtoPlaceholders.forEach((placeholder, index) => {
        htmlContent = htmlContent.replace(`__MAILTO_PLACEHOLDER_${index}__`, placeholder);
      });
      // Extract the body content from the HTML
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlContent, 'text/html');
      const bodyContent = doc.body.innerHTML;
      
      setContent(bodyContent);
    } catch (err) {
      console.error('Error loading document:', err);
      setError('Failed to load document. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = () => {
    onAccept?.();
    onClose();
  };

  const handleDecline = () => {
    onDecline?.();
    onClose();
  };

  // Full screen mode
  if (displayMode === 'fullscreen') {
    return (
      <Modal 
        isOpen={isOpen} 
        onClose={onClose} 
        size="full" 
        scrollBehavior="inside"
        closeOnOverlayClick={true}
        closeOnEsc={true}
        blockScrollOnMount={false}
      >
        <ModalOverlay bg="blackAlpha.600" zIndex={1400} />
        <ModalContent 
          bg="#232b36" 
          color="white" 
          zIndex={1500}
          maxH="100vh"
          display="flex"
          flexDirection="column"
        >
          <style>{`
            .terms-modal-content ul { padding-left: 2em; }
            .terms-modal-content li { margin-bottom: 0.5em; color: #f5f5f5; }
            .terms-modal-content a.terms-email-link {
              color: #8fdfff;
              text-decoration: underline;
              cursor: pointer;
              word-break: break-all;
            }
            .terms-modal-content { color: #f5f5f5; }
            .terms-modal-content h1 {
              color: #fff;
              font-size: 2em;
              margin-bottom: 0.7em;
              margin-top: 0.2em;
              font-weight: bold;
            }
            .terms-modal-content h2 {
              color: #fff;
              font-size: 1.3em;
              margin-top: 1.5em;
              margin-bottom: 0.7em;
              font-weight: 600;
            }
            .terms-modal-content h3 {
              color: #fff;
              font-size: 1.1em;
              margin-top: 1.2em;
              margin-bottom: 0.5em;
              font-weight: 500;
            }
            .terms-modal-content p {
              margin-top: 0.7em;
              margin-bottom: 0.7em;
              line-height: 1.7;
            }
            .terms-modal-content strong, .terms-modal-content b { color: #fff; }
          `}</style>
          <Flex
            justify="space-between"
            align="center"
            p={4}
            borderBottomWidth="1px"
            borderBottomStyle="solid"
            borderBottomColor="#888"
            bg="#232b36"
            flexShrink={0}
          >
            <Text fontSize="xl" fontWeight="bold" color="white">
              {title}
            </Text>
            {showCloseButton && (
              <IconButton
                aria-label="Close"
                icon={<CloseIcon />}
                variant="ghost"
                onClick={onClose}
                size="lg"
              />
            )}
          </Flex>
          <ModalBody 
            p={6} 
      
          >
            {loading && (
              <Flex justify="center" align="center" height="200px">
                <Spinner size="xl" />
              </Flex>
            )}
            {error && (
              <Alert status="error" mb={4}>
                <AlertIcon />
                {error}
              </Alert>
            )}
            {!loading && !error && content && (
              <Box className="terms-modal-content" dangerouslySetInnerHTML={{ __html: content }} style={{border: '1px solid #666', borderRadius: '10px', height: '80vh', overflow: 'auto', width: '70%', margin: '0 auto', padding: '20px'}} />
            )}
          </ModalBody>
          {showAcceptDecline && (
            <ModalFooter bg="#232b36" borderTopWidth="1px" borderTopStyle="solid" borderTopColor="#888">
              <Button variant="outline" onClick={handleDecline}>
                {declineText}
              </Button>
              <Button colorScheme="blue" ml={3} onClick={handleAccept}>
                {acceptText}
              </Button>
            </ModalFooter>
          )}
        </ModalContent>
      </Modal>
    );
  }

  // Popup mode
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      size="xl"
      scrollBehavior="inside"
      closeOnOverlayClick={true}
      closeOnEsc={true}
      blockScrollOnMount={false}
    >
      <ModalOverlay bg="blackAlpha.600" zIndex={1400} />
      <ModalContent maxW="800px" maxH="80vh" zIndex={1500}>
        <ModalHeader>
          <Flex justify="space-between" align="center" borderBottomWidth="1px" borderBottomStyle="solid" borderBottomColor="#888" pb={2}>
            <Text>{title}</Text>
            {showCloseButton && (
              <IconButton
                aria-label="Close"
                icon={<CloseIcon />}
                variant="ghost"
                onClick={onClose}
                size="sm"
              />
            )}
          </Flex>
        </ModalHeader>
        
        <ModalBody 
          overflowY="auto" 
          maxH="60vh"
          css={{
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#2d3748',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#4a5568',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: '#718096',
            },
          }}
        >
          <style>{`
            .terms-modal-content ul { padding-left: 2em; }
            .terms-modal-content li { margin-bottom: 0.5em; color: #f5f5f5; }
            .terms-modal-content a.terms-email-link {
              color: #8fdfff;
              text-decoration: underline;
              cursor: pointer;
              word-break: break-all;
            }
            .terms-modal-content { color: #f5f5f5; }
            .terms-modal-content h1 {
              color: #fff;
              font-size: 2em;
              margin-bottom: 0.7em;
              margin-top: 0.2em;
              font-weight: bold;
            }
            .terms-modal-content h2 {
              color: #fff;
              font-size: 1.3em;
              margin-top: 1.5em;
              margin-bottom: 0.7em;
              font-weight: 600;
            }
            .terms-modal-content h3 {
              color: #fff;
              font-size: 1.1em;
              margin-top: 1.2em;
              margin-bottom: 0.5em;
              font-weight: 500;
            }
            .terms-modal-content p {
              margin-top: 0.7em;
              margin-bottom: 0.7em;
              line-height: 1.7;
            }
            .terms-modal-content strong, .terms-modal-content b { color: #fff; }
          `}</style>
          {loading && (
            <Flex justify="center" align="center" height="200px">
              <Spinner size="xl" />
            </Flex>
          )}
          
          {error && (
            <Alert status="error" mb={4}>
              <AlertIcon />
              {error}
            </Alert>
          )}
          
          {!loading && !error && content && (
            <Box className="terms-modal-content" dangerouslySetInnerHTML={{ __html: content }} />
          )}
        </ModalBody>

        {showAcceptDecline && (
          <ModalFooter>
            <Button variant="outline" mr={3} onClick={handleDecline}>
              {declineText}
            </Button>
            <Button colorScheme="blue" onClick={handleAccept}>
              {acceptText}
            </Button>
          </ModalFooter>
        )}
      </ModalContent>
    </Modal>
  );
};

export default Terms; 